import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ClientDetailsService } from 'src/app/services/client-details.service';
import { ClientDetails } from 'src/app/shared/models/client/client-details';
import { UserIdentifiers } from 'src/app/shared/models/users/user-identifiers';

@Component({
  selector: 'app-user-kyc-profile',
  templateUrl: './user-kyc-profile.component.html',
  styleUrls: ['./user-kyc-profile.component.scss'],
})
export class UserKycProfileComponent implements OnInit, OnChanges, OnDestroy {
  @Input() userIdentifiers: UserIdentifiers;

  kycForm!: FormGroup;
  clientDetails: ClientDetails;
  dataExists: boolean = false;
  loading: boolean = true;
  lastSyncDate: string;
  errorResponse: HttpErrorResponse;
  showError: boolean = false;
  noDataMessage: string = 'No synced data available for this user';

  private incomeKeys = ['IncomeR100k', 'IncomeR250k', 'IncomeR500k', 'IncomeR1milLess', 'IncomeR1milMore'] as const;

  private subs: Subscription[] = [];
  private isUpdatingIncome = false;

  constructor(
    private fb: FormBuilder,
    private clientDetailsService: ClientDetailsService
  ) {}

  ngOnChanges(): void {
    this.showError = false;
    this.populateValues();
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  ngOnInit(): void {
    this.kycForm = this.fb.group({
      Personal: this.fb.group({
        Title: [null],
        FirstName: [null],
        MiddleNames: [null],
        LastName: [null],
        FullName: [null],
        Gender: [null],
        Email: [null, [Validators.email]],
        PhoneNumber: [null],
        DateOfBirth: [null], // 'YYYY-MM-DD'
        IdNumber: [null],
        TaxNumber: [null],
        CountryOfBirth: [null],
        TownOfBirth: [null],
        Nationality: [null],
        CIF: [null],
      }),

      Address: this.fb.group({
        Address: [null], // raw/unparsed
        AddressLine1: [null],
        AddressLine2: [null],
        AddressCity: [null],
        AddressAreaCode: [null],
        AddressProvince: [null],
        AddressCountry: [null],
      }),

      Employment: this.fb.group({
        Occupation: [null],
        EmploymentDesignation: [null],
        Employer: [null],
        EmploymentDescription: [null],
      }),

      Income: this.fb.group(
        {
          IncomeR100k: [false],
          IncomeR250k: [false],
          IncomeR500k: [false],
          IncomeR1milLess: [false],
          IncomeR1milMore: [false],
        },
        {
          // Prevent multiple selections (<= 1 checked)
          validators: this.exactlyOneTrueValidator(['IncomeR100k', 'IncomeR250k', 'IncomeR500k', 'IncomeR1milLess', 'IncomeR1milMore']),
          // If you want exactly one required, replace with:
          // validators: exactlyOneTrueValidator([...])
        }
      ),

      SourceOfFunds: this.fb.group({
        SOF: [null],
        SOFTrust: [false],
        SOFIncome: [false],
        SOFProperty: [false],
        SOFShares: [false],
        SOFInvestment: [false],
        SOFCompany: [false],
        SOFInvestments: [false],
        SOFFamily: [false],
        SOFBusiness: [false],
        SOFSports: [false],
        SOFInheritance: [false],
        SOFLoan: [false],
        SOFGifts: [false],
        SOFLottery: [false],
        SOFCasino: [false],
        SOFSavings: [false],
        SOFDivorce: [false],
        SOFOther: [false],
        SOFDescription: [null],
      }),
    });

    // (Optional but recommended) Auto-uncheck other income boxes if one is checked
    this.setupIncomeExclusivity();
  }

  loadIfClientDetailsHaveValues(details: ClientDetails) {
    this.loading = false;
    if (!details) {
      this.dataExists = false;
      return;
    }
    this.dataExists = true;
    this.patchClientDetails(details);
  }

  private populateValues() {
    this.loading = true;
    const sub = this.clientDetailsService.GetClientDetails(this.userIdentifiers).subscribe({
      next: (details: ClientDetails) => {
        this.loadIfClientDetailsHaveValues(details);
      },
      error: (err) => {
        this.loading = false;
        this.parseAndDisplayError(err);
      },
    });
    this.subs.push(sub);
  }

  pullClientDetails(): void {
    this.loading = true;
    const sub = this.clientDetailsService.SyncClientDetails(this.userIdentifiers).subscribe({
      next: (details: ClientDetails) => {
        this.loadIfClientDetailsHaveValues(details);
      },
      error: (err) => {
        this.loading = false;
        this.parseAndDisplayError(err);
      },
    });
    this.subs.push(sub);
  }

  /** If you ever want exactly one required, use this instead */
  private exactlyOneTrueValidator(keys: string[]) {
    return (group: AbstractControl): ValidationErrors | null => {
      const count = keys.map((k) => group.get(k)?.value === true).filter(Boolean).length;
      return count === 1 ? null : { incomeMustBeSelected: true, count };
    };
  }

  /** Prevent multiple true by unchecking others when one is turned on */
  private setupIncomeExclusivity(): void {
    const incomeGroup = this.kycForm.get('Income') as FormGroup;
    this.incomeKeys.forEach((key) => {
      const ctrl = incomeGroup.get(key) as FormControl;
      const sub = ctrl.valueChanges.subscribe((checked: boolean) => {
        if (this.isUpdatingIncome || !checked) return;
        this.isUpdatingIncome = true;
        // Uncheck all other boxes
        this.incomeKeys.filter((k) => k !== key).forEach((k) => incomeGroup.get(k)?.setValue(false, { emitEvent: false }));
        this.isUpdatingIncome = false;
        // Touch the group so error messages update promptly
        incomeGroup.markAsTouched();
        incomeGroup.updateValueAndValidity({ onlySelf: true });
      });
      this.subs.push(sub);
    });
  }

  private patchClientDetails(client: ClientDetails): void {
    this.lastSyncDate = client.lastSyncDate;
    this.kycForm.patchValue({
      Personal: {
        FirstName: client.firstName,
        MiddleNames: client.middleNames,
        LastName: client.lastName,
        FullName: client.fullName,
        Gender: client.gender,
        Title: client.title,
        IdNumber: client.idNumber,
        DateOfBirth: client.dateOfBirth,
        CountryOfBirth: client.countryOfBirth,
        TownOfBirth: client.townOfBirth,
        Nationality: client.nationality,
        TaxNumber: client.taxNumber,
        Email: client.email,
        PhoneNumber: client.phoneNumber,
        CIF: client.cif,
      },
      Address: {
        Address: client.address,
        AddressLine1: client.addressLine1,
        AddressLine2: client.addressLine2,
        AddressCity: client.addressCity,
        AddressAreaCode: client.addressAreaCode,
        AddressProvince: client.addressProvince,
        AddressCountry: client.addressCountry,
      },
      Employment: {
        Occupation: client.occupation,
        EmploymentDesignation: client.employmentDesignation,
        Employer: client.employer,
        EmploymentDescription: client.employmentDescription,
      },
      Income: {
        IncomeR100k: client.incomeR100k,
        IncomeR250k: client.incomeR250k,
        IncomeR500k: client.incomeR500k,
        IncomeR1milLess: client.incomeR1milLess,
        IncomeR1milMore: client.incomeR1milMore,
      },
      SourceOfFunds: {
        SOF: client.sof,
        SOFDivorce: client.sofDivorce,
        SOFTrust: client.sofTrust,
        SOFIncome: client.sofIncome,
        SOFProperty: client.sofProperty,
        SOFShares: client.sofShares,
        SOFInvestment: client.sofInvestment,
        SOFCompany: client.sofCompany,
        SOFInvestments: client.sofInvestments,
        SOFFamily: client.sofFamily,
        SOFBusiness: client.sofBusiness,
        SOFSports: client.sofSports,
        SOFInheritance: client.sofInheritance,
        SOFLoan: client.sofLoan,
        SOFGifts: client.sofGifts,
        SOFLottery: client.sofLottery,
        SOFCasino: client.sofCasino,
        SOFSavings: client.sofSavings,
        SOFOther: client.sofOther,
        SOFDescription: client.sofDescription,
      },
    });
  }

  submitHandler(): void {
    if (this.kycForm.invalid) {
      this.kycForm.markAllAsTouched();
      return;
    }
    const payload = this.kycForm.value;
    // TODO: submit payload
    console.log('Submitting payload:', payload);
  }

  parseAndDisplayError(error: any) {
    this.errorResponse = error;
    this.showError = true;
  }
}
